import { useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { useUserMeQuery } from '@/graphql';
import type { User_FragmentFragment, User_Settings_FragmentFragment } from '@/graphql';
import { FeatureFlagsNamesEnum, ProfessionTypesEnum } from '@/type';
import { useAuth } from '@/contexts/auth-context';

interface FeatureFlags {
  dailyTipperEnabled: boolean | null;
  classroomsEnabled: boolean | null;
  humeoAppEnabled: boolean | null;
  paymentEnabled: boolean | null;
  inAppPaymentEnabled: boolean | null;
  cookiesEnabled: boolean | null;
  subsDiscountsEnabled: boolean | null;
  inviteFriendsEnabled: boolean | null;
  crAvaGroupEnabled: boolean | null;
  cmeAcademyEnabled: boolean | null;
  menuItemPremiumCodeEnabled: boolean | null;
  menuItemQuiztimeEnabled: boolean | null;
  me: User_FragmentFragment | null | undefined;
  userSettings: User_Settings_FragmentFragment | null | undefined;
  loading: boolean;
}

export const useFeatureFlags = (): FeatureFlags => {
  const { token } = useAuth();
  const { data, loading } = useUserMeQuery({
    skip: !token,
  });

  const featureFlagsObj = useMemo(() => keyBy(data?.featureFlags, 'name'), [data]);

  // Check if user is RFA DigiMed (MedicalAssistant or TraineeToNurse)
  const isRfaDigiMed = useMemo(() => {
    const professionType = data?.me?.profession?.type?.type;
    return (
      professionType === ProfessionTypesEnum.MedicalAssistant ||
      professionType === ProfessionTypesEnum.TraineeToNurse
    );
  }, [data?.me?.profession?.type?.type]);

  return {
    dailyTipperEnabled: featureFlagsObj[FeatureFlagsNamesEnum.DAILY_TIPPER]?.enabled !== false,
    classroomsEnabled: featureFlagsObj[FeatureFlagsNamesEnum.CLASS_ROOMS]?.enabled !== false,
    humeoAppEnabled: featureFlagsObj[FeatureFlagsNamesEnum.HUMEO_APP]?.enabled !== false,
    paymentEnabled: featureFlagsObj[FeatureFlagsNamesEnum.PAYMENT]?.enabled !== false,
    inAppPaymentEnabled: featureFlagsObj[FeatureFlagsNamesEnum.IN_APP_PAYMENT]?.enabled !== false,
    cookiesEnabled: featureFlagsObj[FeatureFlagsNamesEnum.COOKIES]?.enabled !== false,
    subsDiscountsEnabled: featureFlagsObj[FeatureFlagsNamesEnum.SUBS_DISCOUNTS]?.enabled !== false,
    inviteFriendsEnabled: featureFlagsObj[FeatureFlagsNamesEnum.INVITE_FRIENDS]?.enabled !== false,
    crAvaGroupEnabled:
      featureFlagsObj[FeatureFlagsNamesEnum.CLASSROOM_AVA_GROUP]?.enabled !== false,
    cmeAcademyEnabled:
      featureFlagsObj[FeatureFlagsNamesEnum.CME_ACADEMY]?.enabled !== false,
    menuItemPremiumCodeEnabled:
      isRfaDigiMed ||
      featureFlagsObj[FeatureFlagsNamesEnum.MENU_ITEM_PREMIUM_CODE]?.enabled !== false,
    menuItemQuiztimeEnabled:
      isRfaDigiMed ||
      featureFlagsObj[FeatureFlagsNamesEnum.MENU_ITEM_QUIZTIME]?.enabled !== false,
    me: data?.me,
    userSettings: data?.me?.settings,
    loading,
  };
};
